import {
  UserRegistrationPayloadSchema,
  useProfileByKeycloakId,
  useRegisterUser,
} from "@/api/auth";
import { PropsWithChildren } from "react";
import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import { DefaultButton } from "./ui/button";
import intervalLogo from "@/assets/athena-new-logo.png";
import { Loading } from "./ui/loading";
import { P } from "./ui/typography";
import { useAuth } from "@/features/auth/useAuth";
import { LucideXCircle } from "lucide-react";

export function UserGate({ children }: PropsWithChildren) {
  const auth = useAuth();
  const user = auth.getUser();
  const {
    data: profile,
    isPending: isLoadingProfile,
    isError: isProfileError,
    error,
  } = useProfileByKeycloakId({
    keycloakId: user?.id,
    enabled: auth.isReady && !!user?.id,
  });
  const { mutate, isPending } = useRegisterUser();

  if (!auth.isReady || !user.id) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (isLoadingProfile) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loading />
      </div>
    );
  }

  function handleLogout() {
    auth.logout();
  }

  function handleRegister() {
    const newUserData: UserRegistrationPayloadSchema = {
      email: user?.email ?? "",
      username: user?.username ?? "",
      firstName: user?.firstName ?? "",
      lastName: user?.lastName ?? "",
      keycloakId: user?.id ?? "",
    };
    mutate({
      data: newUserData,
    });
  }

  if (isProfileError) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Card className="m-2 max-w-96 bg-[#090B2F] py-4 text-white">
          <CardHeader className="flex items-center justify-center text-3xl font-bold text-axiata-red">
            <LucideXCircle height={96} width={96} />
            Failed
          </CardHeader>
          <CardContent className="text-center">
            <P>
              {(error?.response?.data as { data: string })?.data ??
                "An error occurred."}
            </P>
          </CardContent>
          <CardFooter className="flex justify-center gap-x-2">
            <DefaultButton
              mood="axiata-blue"
              onClick={handleLogout}
              loading={isPending}
            >
              Logout
            </DefaultButton>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (profile) {
    return children;
  }

  return (
    <div className="flex min-h-svh items-center justify-center">
      <Card className="m-2 max-w-96 bg-[#090B2F] text-white">
        <CardHeader>
          <img src={intervalLogo} alt="Interval: Integrated & Validated" />
        </CardHeader>
        <CardContent className="text-center">
          <P>Welcome to the Interval Website.</P>
          <P>
            Please click "Continue" to enjoy the features of the Interval
            Website.
          </P>
        </CardContent>
        <CardFooter className="flex justify-center gap-x-2">
          <DefaultButton
            mood="axiata-red"
            onClick={handleLogout}
            loading={isPending}
          >
            Cancel
          </DefaultButton>
          <DefaultButton
            mood="axiata-blue"
            onClick={handleRegister}
            loading={isPending}
          >
            Continue
          </DefaultButton>
        </CardFooter>
      </Card>
    </div>
  );
}
